import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  History, Clock, User, ArrowRight, 
  AlertTriangle, CheckCircle, PlayCircle, 
  Bug, Wrench, XCircle, RefreshCw
} from "lucide-react";
import { API } from "@/App";

const FIELD_ICONS = {
  status: PlayCircle,
  severity: AlertTriangle,
  classification: Bug,
  category: Bug,
  mode: PlayCircle,
  frequency: RefreshCw,
  version: History,
  default: History
};

const FIELD_COLORS = {
  status: "text-blue-400",
  severity: "text-red-400",
  classification: "text-purple-400",
  category: "text-emerald-400",
  mode: "text-cyan-400",
  frequency: "text-orange-400",
  version: "text-gray-400",
  default: "text-muted-foreground"
};

const STATUS_COLORS = {
  "Open": "bg-red-500/20 text-red-400 border-red-500/30",
  "In Progress": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  "Fixed": "bg-green-500/20 text-green-400 border-green-500/30",
  "Verified": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Closed": "bg-gray-500/20 text-gray-400 border-gray-500/30",
  "Won't Fix": "bg-purple-500/20 text-purple-400 border-purple-500/30"
};

const SEVERITY_COLORS = {
  "Critical": "bg-red-500/20 text-red-400",
  "High": "bg-orange-500/20 text-orange-400",
  "Medium": "bg-yellow-500/20 text-yellow-400",
  "Low": "bg-blue-500/20 text-blue-400"
};

function TimelineItem({ change, isFirst, isLast }) {
  const Icon = FIELD_ICONS[change.field] || FIELD_ICONS.default;
  const color = FIELD_COLORS[change.field] || FIELD_COLORS.default;
  
  const formatValue = (value, field) => {
    if (!value) return "None";
    if (Array.isArray(value)) return value.join(", ") || "None";
    return value;
  };
  
  const getValueStyle = (value, field) => {
    if (field === "status") return STATUS_COLORS[value] || "";
    if (field === "severity") return SEVERITY_COLORS[value] || "";
    return "";
  };
  
  const formatTimestamp = (ts) => {
    if (!ts) return "Unknown time";
    const date = new Date(ts);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="relative flex gap-4">
      {/* Timeline line */}
      <div className="flex flex-col items-center">
        <div className={`w-10 h-10 rounded-full bg-muted/50 border-2 border-border/60 flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        {!isLast && (
          <div className="w-0.5 flex-1 bg-border/40 min-h-[20px]" />
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="bg-muted/20 rounded-sm border border-border/40 p-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
              {change.field?.replace(/_/g, " ")}
            </span>
            <span className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTimestamp(change.timestamp)}
            </span>
          </div>
          
          {/* Change values */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm px-2 py-0.5 rounded-sm border ${getValueStyle(change.old_value, change.field) || 'bg-muted/30 border-border/40 text-muted-foreground'}`}>
              {formatValue(change.old_value, change.field)}
            </span>
            <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className={`text-sm px-2 py-0.5 rounded-sm border font-semibold ${getValueStyle(change.new_value, change.field) || 'bg-primary/10 border-primary/30 text-primary'}`}>
              {formatValue(change.new_value, change.field)}
            </span>
          </div>
          
          {/* Changed by */}
          {change.changed_by && (
            <div className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <User className="w-3 h-3" />
              <span>{change.changed_by}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BugTimeline({ bugId, createdAt }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/bugs/${bugId}/history`);
        // Sort by timestamp, newest first for display but we'll reverse for timeline
        const sortedHistory = (response.data || []).sort((a, b) => {
          const timeA = new Date(a.timestamp || 0).getTime();
          const timeB = new Date(b.timestamp || 0).getTime();
          return timeA - timeB; // oldest first for timeline
        });
        setHistory(sortedHistory);
        setError(null);
      } catch (err) {
        console.error("Error fetching bug history:", err);
        setError("Failed to load history");
      } finally {
        setLoading(false);
      }
    };

    if (bugId) {
      fetchHistory();
    }
  }, [bugId]);

  if (loading) {
    return (
      <Card className="bg-card border-border/40" data-testid="bug-timeline">
        <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
          <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Change History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-card border-border/40" data-testid="bug-timeline">
        <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
          <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Change History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 text-center text-muted-foreground font-mono text-sm">
          {error}
        </CardContent>
      </Card>
    );
  }

  // Add creation as first event
  const fullHistory = [
    {
      field: "created",
      old_value: null,
      new_value: "Bug Reported",
      timestamp: createdAt,
      changed_by: "System"
    },
    ...history
  ];

  return (
    <Card className="bg-card border-border/40" data-testid="bug-timeline">
      <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
        <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <History className="w-5 h-5 text-primary" />
          Change History
          <span className="text-xs text-muted-foreground font-normal ml-auto">
            {history.length} change{history.length !== 1 ? 's' : ''}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {fullHistory.length === 1 ? (
          <div className="text-center text-muted-foreground font-mono text-sm py-4">
            No changes recorded yet
          </div>
        ) : (
          <div className="relative">
            {fullHistory.map((change, index) => (
              <TimelineItem
                key={`${change.field}-${change.timestamp}-${index}`}
                change={change}
                isFirst={index === 0}
                isLast={index === fullHistory.length - 1}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
